import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { landingPathForRole } from '../../lib/roleRouting';

/** Au-dela, on considere que le retour de Google n'a pas abouti. */
const SESSION_TIMEOUT_MS = 15_000;

/** Lit une erreur OAuth renvoyee dans la query ou dans le fragment de l'URL. */
function readOAuthError(): string | null {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const code = query.get('error') ?? hash.get('error');
  if (!code) return null;
  if (code === 'access_denied') {
    return 'Connexion avec Google annulée.';
  }
  return 'La connexion avec Google a échoué. Réessayez ou utilisez votre email et mot de passe.';
}

/**
  Page de retour apres « Continuer avec Google ».

  Le client Supabase lit la session dans l'URL (`detectSessionInUrl`) et
  l'effet d'amorcage d'App.tsx charge alors l'utilisateur puis son profil. On
  attend ce profil avant d'orienter selon le role, comme apres une connexion
  par mot de passe : sans lui, un membre du personnel partirait vers l'espace
  parent.
*/
const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const { user, profile, profileLoaded } = useAuthStore();
  const [error, setError] = useState<string | null>(() => readOAuthError());

  useEffect(() => {
    if (error || !user || !profileLoaded) return;
    navigate(landingPathForRole(profile?.role), { replace: true });
  }, [error, user, profile?.role, profileLoaded, navigate]);

  useEffect(() => {
    if (error || user) return;
    const timer = window.setTimeout(
      () => setError('La connexion avec Google a échoué. Réessayez ou utilisez votre email et mot de passe.'),
      SESSION_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [error, user]);

  if (error) {
    return (
      <div className="animate-fade-in">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Connexion</h2>
        <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm mb-6" role="alert">
          {error}
        </div>
        <Link to="/login" className="btn-primary inline-block">
          Retour à la connexion
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in flex flex-col items-center py-10 text-gray-600">
      <Loader2 className="animate-spin h-8 w-8 mb-4 text-primary-600" />
      Connexion en cours...
    </div>
  );
};

export default AuthCallbackPage;
